import { Prisma, type PrismaClient } from '@prisma/client';
import { Router, type Request } from 'express';
import { z } from 'zod';
import { audit } from '../../core/audit.js';
import type { Config } from '../../core/config.js';
import { HttpError } from '../../core/errors.js';
import { asyncHandler, clientIp, pageParams, parse } from '../../core/http.js';
import { idempotent } from '../../core/idempotency.js';
import { authenticate, requirePermission } from '../auth/auth.middleware.js';
import { isDebitNormal } from './chart.js';
import { isoDate, toDate } from './periods.js';
import {
  accountBalances,
  createDraft,
  postDraft,
  postEntry,
  reverseEntry,
  updateDraft,
} from './posting.service.js';

interface Deps {
  db: PrismaClient;
  config: Config;
}

const D = (v: Prisma.Decimal.Value) => new Prisma.Decimal(v);
const dateStr = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD');
const money = z.union([z.string(), z.number()]).transform((v) => String(v));

const lineSchema = z.object({
  accountId: z.number().int().positive(),
  debit: money.optional(),
  credit: money.optional(),
  memo: z.string().max(300).optional(),
});
const journalSchema = z.object({
  date: dateStr,
  description: z.string().trim().min(3).max(500),
  reference: z.string().trim().max(100).optional(),
  lines: z.array(lineSchema).min(2).max(200),
});

export function createAccountingRouter({ db, config }: Deps) {
  const router = Router();
  router.use('/accounting', authenticate({ db, config }), idempotent(db));

  const actor = (req: Request) => ({
    companyId: req.auth!.companyId,
    userId: req.auth!.userId,
    ip: clientIp(req),
  });

  // ───── chart of accounts ─────
  const accountView = (
    a: Prisma.AccountGetPayload<object>,
    bal?: { debit: Prisma.Decimal; credit: Prisma.Decimal },
  ) => ({
    id: a.id,
    code: a.code,
    name: a.name,
    type: a.type,
    subtype: a.subtype,
    systemKey: a.systemKey,
    isSystem: a.isSystem,
    isActive: a.isActive,
    isCash: a.isCash,
    cashFlowCategory: a.cashFlowCategory,
    description: a.description,
    ...(bal
      ? {
          balance: (isDebitNormal(a.type)
            ? bal.debit.minus(bal.credit)
            : bal.credit.minus(bal.debit)
          ).toFixed(4),
        }
      : {}),
  });

  router.get(
    '/accounting/accounts',
    requirePermission(
      'accounting.account.view',
      'accounting.journal.create',
      'accounting.journal.view',
    ),
    asyncHandler(async (req, res) => {
      const q = req.query as Record<string, string | undefined>;
      const where: Prisma.AccountWhereInput = {
        companyId: req.auth!.companyId,
        ...(q.type ? { type: q.type } : {}),
        ...(q.active === 'true' ? { isActive: true } : {}),
        ...(q.search
          ? { OR: [{ code: { contains: q.search } }, { name: { contains: q.search } }] }
          : {}),
      };
      const accounts = await db.account.findMany({ where, orderBy: { code: 'asc' } });
      const withBalances = q.balances === 'true';
      const balances = withBalances ? await accountBalances(db, req.auth!.companyId) : [];
      res.json({
        items: accounts.map((a) =>
          accountView(
            a,
            withBalances
              ? (balances.find((b) => b.accountId === a.id) ?? { debit: D(0), credit: D(0) })
              : undefined,
          ),
        ),
      });
    }),
  );

  const newAccountSchema = z.object({
    code: z
      .string()
      .trim()
      .regex(/^\d{4,6}$/, 'Use a 4-6 digit code'),
    name: z.string().trim().min(2).max(150),
    type: z.enum(['asset', 'liability', 'equity', 'revenue', 'expense']),
    subtype: z.string().trim().max(30).optional(),
    cashFlowCategory: z.enum(['operating', 'investing', 'financing']).nullable().optional(),
    description: z.string().trim().max(300).optional(),
  });

  router.post(
    '/accounting/accounts',
    requirePermission('accounting.account.create'),
    asyncHandler(async (req, res) => {
      const body = parse(newAccountSchema, req.body);
      const dup = await db.account.findUnique({
        where: { companyId_code: { companyId: req.auth!.companyId, code: body.code } },
      });
      if (dup) throw new HttpError(409, 'CODE_TAKEN', `Account code ${body.code} is already used`);
      const account = await db.$transaction(async (tx) => {
        const created = await tx.account.create({
          data: { companyId: req.auth!.companyId, ...body },
        });
        await audit(tx, actor(req), {
          action: 'account.created',
          entity: 'account',
          entityId: created.id,
          after: accountView(created),
        });
        return created;
      });
      res.status(201).json(accountView(account));
    }),
  );

  router.patch(
    '/accounting/accounts/:id',
    requirePermission('accounting.account.edit'),
    asyncHandler(async (req, res) => {
      const id = Number(req.params.id);
      const body = parse(
        newAccountSchema
          .omit({ code: true })
          .partial()
          .extend({ isActive: z.boolean().optional() }),
        req.body,
      );
      const before = await db.account.findFirst({ where: { id, companyId: req.auth!.companyId } });
      if (!before) throw new HttpError(404, 'NOT_FOUND', 'Account not found');
      if (before.isSystem) throw new HttpError(403, 'SYSTEM_ACCOUNT', 'System accounts are locked');
      if (before.isCash)
        throw new HttpError(
          403,
          'CASH_ACCOUNT',
          'Change bank and payment accounts under Banking & Payments',
        );
      if (
        body.type &&
        body.type !== before.type &&
        (await db.journalLine.count({ where: { accountId: id } })) > 0
      ) {
        throw new HttpError(
          409,
          'HAS_ACTIVITY',
          'The type cannot change once the account has postings',
        );
      }
      const after = await db.$transaction(async (tx) => {
        const updated = await tx.account.update({ where: { id }, data: body });
        await audit(tx, actor(req), {
          action: 'account.updated',
          entity: 'account',
          entityId: id,
          before: accountView(before),
          after: accountView(updated),
        });
        return updated;
      });
      res.json(accountView(after));
    }),
  );

  // ───── fiscal periods ─────
  router.get(
    '/accounting/periods',
    requirePermission('accounting.period.view'),
    asyncHandler(async (req, res) => {
      const periods = await db.fiscalPeriod.findMany({
        where: { companyId: req.auth!.companyId },
        orderBy: { startDate: 'desc' },
      });
      const years = await db.financialYear.findMany({
        where: { companyId: req.auth!.companyId },
        orderBy: { startDate: 'desc' },
      });
      res.json({ items: periods, years });
    }),
  );

  router.post(
    '/accounting/periods/:id/close',
    requirePermission('accounting.period.close'),
    asyncHandler(async (req, res) => {
      const id = Number(req.params.id);
      const period = await db.fiscalPeriod.findFirst({
        where: { id, companyId: req.auth!.companyId },
      });
      if (!period) throw new HttpError(404, 'NOT_FOUND', 'Period not found');
      if (period.status === 'closed')
        throw new HttpError(409, 'ALREADY_CLOSED', 'Period is already closed');
      const drafts = await db.journalEntry.count({
        where: {
          companyId: period.companyId,
          status: 'draft',
          entryDate: { gte: period.startDate, lte: period.endDate },
        },
      });
      if (drafts > 0)
        throw new HttpError(
          409,
          'DRAFTS_EXIST',
          `${drafts} draft journal(s) dated in this period must be posted or deleted first`,
        );
      const updated = await db.$transaction(async (tx) => {
        const p = await tx.fiscalPeriod.update({
          where: { id },
          data: {
            status: 'closed',
            closedAt: new Date(),
            closedBy: req.auth!.userId,
            reopenReason: null,
          },
        });
        await audit(tx, actor(req), {
          action: 'period.closed',
          entity: 'fiscal_period',
          entityId: id,
          before: period,
          after: p,
        });
        return p;
      });
      res.json(updated);
    }),
  );

  router.post(
    '/accounting/periods/:id/reopen',
    requirePermission('accounting.period.reopen'),
    asyncHandler(async (req, res) => {
      const id = Number(req.params.id);
      const { reason } = parse(z.object({ reason: z.string().trim().min(5).max(300) }), req.body);
      const period = await db.fiscalPeriod.findFirst({
        where: { id, companyId: req.auth!.companyId },
      });
      if (!period) throw new HttpError(404, 'NOT_FOUND', 'Period not found');
      if (period.status === 'open')
        throw new HttpError(409, 'ALREADY_OPEN', 'Period is already open');
      const updated = await db.$transaction(async (tx) => {
        const p = await tx.fiscalPeriod.update({
          where: { id },
          data: { status: 'open', closedAt: null, reopenReason: reason },
        });
        await audit(tx, actor(req), {
          action: 'period.reopened',
          entity: 'fiscal_period',
          entityId: id,
          before: period,
          after: { ...p, reason },
        });
        return p;
      });
      res.json(updated);
    }),
  );

  /** Year-end: moves the year's profit or loss into Retained Earnings, then closes every period. */
  router.post(
    '/accounting/years/:id/close',
    requirePermission('accounting.period.close'),
    asyncHandler(async (req, res) => {
      const id = Number(req.params.id);
      const fy = await db.financialYear.findFirst({
        where: { id, companyId: req.auth!.companyId },
      });
      if (!fy) throw new HttpError(404, 'NOT_FOUND', 'Financial year not found');
      if (fy.status === 'closed')
        throw new HttpError(409, 'ALREADY_CLOSED', 'This year is already closed');

      const result = await db.$transaction(async (tx) => {
        const accounts = await tx.account.findMany({
          where: { companyId: fy.companyId, type: { in: ['revenue', 'expense'] } },
        });
        const balances = await accountBalances(tx, fy.companyId, {
          from: fy.startDate,
          to: fy.endDate,
        });
        const lines: { accountId: number; debit?: string; credit?: string; memo?: string }[] = [];
        let net = D(0); // profit is positive
        for (const acc of accounts) {
          const b = balances.find((x) => x.accountId === acc.id);
          if (!b) continue;
          const bal = acc.type === 'revenue' ? b.credit.minus(b.debit) : b.debit.minus(b.credit);
          if (bal.isZero()) continue;
          net = acc.type === 'revenue' ? net.plus(bal) : net.minus(bal);
          // Zero the account: revenue balances are credits, so debit them; expenses the opposite.
          if (acc.type === 'revenue')
            lines.push(
              bal.isPositive()
                ? { accountId: acc.id, debit: bal.toFixed(4) }
                : { accountId: acc.id, credit: bal.abs().toFixed(4) },
            );
          else
            lines.push(
              bal.isPositive()
                ? { accountId: acc.id, credit: bal.toFixed(4) }
                : { accountId: acc.id, debit: bal.abs().toFixed(4) },
            );
        }
        let entry = null;
        if (lines.length > 0) {
          lines.push(
            net.isPositive()
              ? { accountId: 0, credit: net.toFixed(4) }
              : { accountId: 0, debit: net.abs().toFixed(4) },
          );
          const retained = await tx.account.findFirstOrThrow({
            where: { companyId: fy.companyId, systemKey: 'RETAINED' },
          });
          for (const l of lines) if (l.accountId === 0) l.accountId = retained.id;
          entry = await postEntry(tx, {
            companyId: fy.companyId,
            date: fy.endDate,
            description: `Year-end closing entry ${fy.name}`,
            sourceType: 'closing',
            sourceId: fy.id,
            lines,
            userId: req.auth!.userId,
          });
        }
        await tx.fiscalPeriod.updateMany({
          where: { companyId: fy.companyId, financialYearId: fy.id },
          data: { status: 'closed', closedAt: new Date(), closedBy: req.auth!.userId },
        });
        const closed = await tx.financialYear.update({ where: { id }, data: { status: 'closed' } });
        await audit(tx, actor(req), {
          action: 'year.closed',
          entity: 'financial_year',
          entityId: id,
          after: { netProfit: net.toFixed(4), entryId: entry?.id },
        });
        return { year: closed, netProfit: net.toFixed(4), entryId: entry?.id ?? null };
      });
      res.json(result);
    }),
  );

  // ───── journal entries ─────
  const journalView = (
    e: Prisma.JournalEntryGetPayload<{ include: { lines: { include: { account: true } } } }>,
  ) => ({
    id: e.id,
    entryNo: e.entryNo,
    date: isoDate(e.entryDate),
    description: e.description,
    status: e.status,
    sourceType: e.sourceType,
    sourceId: e.sourceId,
    reference: e.reference,
    total: e.totalAmount.toFixed(4),
    reversesId: e.reversesId,
    reversedById: e.reversedById,
    lines: e.lines
      .sort((a, b) => a.lineNo - b.lineNo)
      .map((l) => ({
        id: l.id,
        accountId: l.accountId,
        accountCode: l.account.code,
        accountName: l.account.name,
        debit: l.debit.toFixed(4),
        credit: l.credit.toFixed(4),
        memo: l.memo,
      })),
  });

  router.get(
    '/accounting/journals',
    requirePermission('accounting.journal.view'),
    asyncHandler(async (req, res) => {
      const { page, pageSize, skip, take } = pageParams(req.query as Record<string, unknown>);
      const q = req.query as Record<string, string | undefined>;
      const where: Prisma.JournalEntryWhereInput = {
        companyId: req.auth!.companyId,
        ...(q.status ? { status: q.status } : {}),
        ...(q.sourceType ? { sourceType: q.sourceType } : {}),
        ...(q.search
          ? {
              OR: [
                { description: { contains: q.search } },
                { entryNo: { contains: q.search } },
                { reference: { contains: q.search } },
              ],
            }
          : {}),
        ...(q.from || q.to
          ? {
              entryDate: {
                ...(q.from ? { gte: toDate(q.from) } : {}),
                ...(q.to ? { lte: toDate(q.to) } : {}),
              },
            }
          : {}),
      };
      const [total, rows] = await Promise.all([
        db.journalEntry.count({ where }),
        db.journalEntry.findMany({
          where,
          orderBy: [{ entryDate: 'desc' }, { id: 'desc' }],
          skip,
          take,
        }),
      ]);
      res.json({
        items: rows.map((e) => ({
          id: e.id,
          entryNo: e.entryNo,
          date: isoDate(e.entryDate),
          description: e.description,
          status: e.status,
          sourceType: e.sourceType,
          total: e.totalAmount.toFixed(4),
          reversed: !!e.reversedById,
        })),
        total,
        page,
        pageSize,
      });
    }),
  );

  const loadJournal = async (companyId: number, id: number) => {
    const e = await db.journalEntry.findFirst({
      where: { id, companyId },
      include: { lines: { include: { account: true } } },
    });
    if (!e) throw new HttpError(404, 'NOT_FOUND', 'Journal entry not found');
    return e;
  };

  router.get(
    '/accounting/journals/:id',
    requirePermission('accounting.journal.view'),
    asyncHandler(async (req, res) => {
      res.json(journalView(await loadJournal(req.auth!.companyId, Number(req.params.id))));
    }),
  );

  router.post(
    '/accounting/journals',
    requirePermission('accounting.journal.create'),
    asyncHandler(async (req, res) => {
      const body = parse(journalSchema, req.body);
      const entry = await db.$transaction(async (tx) => {
        const draft = await createDraft(tx, {
          companyId: req.auth!.companyId,
          sourceType: 'manual',
          userId: req.auth!.userId,
          ...body,
        });
        await audit(tx, actor(req), {
          action: 'journal.draft_created',
          entity: 'journal_entry',
          entityId: draft.id,
          after: { ...body },
        });
        return draft;
      });
      res.status(201).json(journalView(await loadJournal(req.auth!.companyId, entry.id)));
    }),
  );

  router.put(
    '/accounting/journals/:id',
    requirePermission('accounting.journal.create'),
    asyncHandler(async (req, res) => {
      const id = Number(req.params.id);
      const body = parse(journalSchema, req.body);
      await db.$transaction(async (tx) => {
        await updateDraft(tx, id, {
          companyId: req.auth!.companyId,
          sourceType: 'manual',
          userId: req.auth!.userId,
          ...body,
        });
        await audit(tx, actor(req), {
          action: 'journal.draft_updated',
          entity: 'journal_entry',
          entityId: id,
          after: { ...body },
        });
      });
      res.json(journalView(await loadJournal(req.auth!.companyId, id)));
    }),
  );

  router.delete(
    '/accounting/journals/:id',
    requirePermission('accounting.journal.delete'),
    asyncHandler(async (req, res) => {
      const id = Number(req.params.id);
      const e = await loadJournal(req.auth!.companyId, id);
      if (e.status !== 'draft')
        throw new HttpError(
          409,
          'IMMUTABLE',
          'Posted entries cannot be deleted. Post a reversal instead.',
        );
      await db.$transaction(async (tx) => {
        await tx.journalLine.deleteMany({ where: { entryId: id } });
        await tx.journalEntry.delete({ where: { id } });
        await audit(tx, actor(req), {
          action: 'journal.draft_deleted',
          entity: 'journal_entry',
          entityId: id,
          before: journalView(e),
        });
      });
      res.status(204).end();
    }),
  );

  router.post(
    '/accounting/journals/:id/post',
    requirePermission('accounting.journal.post'),
    asyncHandler(async (req, res) => {
      const id = Number(req.params.id);
      await db.$transaction(async (tx) => {
        const posted = await postDraft(tx, id, req.auth!.companyId, req.auth!.userId);
        await audit(tx, actor(req), {
          action: 'journal.posted',
          entity: 'journal_entry',
          entityId: id,
          after: { entryNo: posted.entryNo, total: posted.totalAmount.toFixed(4) },
        });
      });
      res.json(journalView(await loadJournal(req.auth!.companyId, id)));
    }),
  );

  router.post(
    '/accounting/journals/:id/reverse',
    requirePermission('accounting.journal.post'),
    asyncHandler(async (req, res) => {
      const id = Number(req.params.id);
      const body = parse(
        z.object({ reason: z.string().trim().min(3).max(200), date: dateStr.optional() }),
        req.body,
      );
      const reversal = await db.$transaction(async (tx) => {
        const r = await reverseEntry(tx, {
          companyId: req.auth!.companyId,
          entryId: id,
          reason: body.reason,
          date: body.date,
          userId: req.auth!.userId,
        });
        await audit(tx, actor(req), {
          action: 'journal.reversed',
          entity: 'journal_entry',
          entityId: id,
          after: { reversalId: r.id, reason: body.reason },
        });
        return r;
      });
      res.status(201).json(journalView(await loadJournal(req.auth!.companyId, reversal.id)));
    }),
  );

  // ───── reports needed to verify the ledger ─────
  router.get(
    '/accounting/reports/trial-balance',
    requirePermission('accounting.report.view'),
    asyncHandler(async (req, res) => {
      const asOf = req.query.asOf ? parse(dateStr, req.query.asOf) : isoDate(new Date());
      const [accounts, balances] = await Promise.all([
        db.account.findMany({
          where: { companyId: req.auth!.companyId },
          orderBy: { code: 'asc' },
        }),
        accountBalances(db, req.auth!.companyId, { to: toDate(asOf) }),
      ]);
      let totalDebit = D(0);
      let totalCredit = D(0);
      const rows = accounts.flatMap((a) => {
        const b = balances.find((x) => x.accountId === a.id);
        if (!b) return [];
        const net = b.debit.minus(b.credit);
        if (net.isZero()) return []; // accounts that net to zero are not listed
        const debit = net.isPositive() ? net : D(0);
        const credit = net.isNegative() ? net.abs() : D(0);
        totalDebit = totalDebit.plus(debit);
        totalCredit = totalCredit.plus(credit);
        return [
          {
            accountId: a.id,
            code: a.code,
            name: a.name,
            type: a.type,
            debit: debit.toFixed(4),
            credit: credit.toFixed(4),
          },
        ];
      });
      res.json({
        asOf,
        rows,
        totalDebit: totalDebit.toFixed(4),
        totalCredit: totalCredit.toFixed(4),
        balanced: totalDebit.equals(totalCredit),
      });
    }),
  );

  router.get(
    '/accounting/reports/general-ledger',
    requirePermission('accounting.ledger.view'),
    asyncHandler(async (req, res) => {
      const q = parse(
        z.object({
          accountId: z.coerce.number().int().positive(),
          from: dateStr.optional(),
          to: dateStr.optional(),
        }),
        req.query,
      );
      const account = await db.account.findFirst({
        where: { id: q.accountId, companyId: req.auth!.companyId },
      });
      if (!account) throw new HttpError(404, 'NOT_FOUND', 'Account not found');
      const debitNormal = isDebitNormal(account.type);
      const opening = q.from
        ? (
            await accountBalances(db, account.companyId, {
              to: new Date(toDate(q.from).getTime() - 86_400_000),
              accountIds: [account.id],
            })
          )[0]
        : undefined;
      const openingBalance = opening
        ? debitNormal
          ? opening.debit.minus(opening.credit)
          : opening.credit.minus(opening.debit)
        : D(0);
      const lines = await db.journalLine.findMany({
        where: {
          accountId: account.id,
          entry: {
            companyId: account.companyId,
            status: 'posted',
            ...(q.from || q.to
              ? {
                  entryDate: {
                    ...(q.from ? { gte: toDate(q.from) } : {}),
                    ...(q.to ? { lte: toDate(q.to) } : {}),
                  },
                }
              : {}),
          },
        },
        include: { entry: true },
        orderBy: [{ entry: { entryDate: 'asc' } }, { entryId: 'asc' }, { lineNo: 'asc' }],
        take: 5000,
      });
      let running = openingBalance;
      const rows = lines.map((l) => {
        running = debitNormal
          ? running.plus(l.debit).minus(l.credit)
          : running.plus(l.credit).minus(l.debit);
        return {
          lineId: l.id,
          entryId: l.entryId,
          entryNo: l.entry.entryNo,
          date: isoDate(l.entry.entryDate),
          description: l.memo ?? l.entry.description,
          sourceType: l.entry.sourceType,
          sourceId: l.entry.sourceId,
          debit: l.debit.toFixed(4),
          credit: l.credit.toFixed(4),
          balance: running.toFixed(4),
        };
      });
      res.json({
        account: { id: account.id, code: account.code, name: account.name, type: account.type },
        from: q.from ?? null,
        to: q.to ?? null,
        openingBalance: openingBalance.toFixed(4),
        closingBalance: running.toFixed(4),
        rows,
      });
    }),
  );

  return router;
}
